import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { ReportsTabPageContainer, StyledTable, ChildRow, StatusDot, ExpandIcon } from './styled';
import { ValidationMessagePopoverGlobalStyle } from '../EnginePage/Engine/Common/styled';
import { EcoSelectStation, SpinCentered } from '@/comp';
import { useTranslation } from 'react-i18next';
import { getColumns } from './columns';
import { Button } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { getBackGroundColorBasedOnStatus, getTextBasedOnStatus } from './ReportPage.utils';
import { handleEditClick } from './columns.utils';
import ReportPopup from './ReportPerformanceModal';
import CreateNewReportModal from './CreateNewReportModal';
import PublishModal from './PublishModal';
import DeleteReportModal from './DeleteReportModal';
import { useStores } from '@/hooks/use-stores';
import dayjs from 'dayjs';
import { ReportGeneratorStatus } from '@/constants/ReportGeneratorStatus.enum';
import { useResponsive } from '@/hooks/useResponsive';

interface ReportRecord {
  id?: string;
  title?: string;
  reportDetails?: string;
  dateCreated?: string;
  createdAt?: string;
  createdDate?: string;
  createdBy?: string;
  reportCreatedBy?: string;
  status?: string;
  versions?: ReportRecord[];
  [key: string]: unknown;
}

interface TransformedChildRecord extends ReportRecord {
  childReportDetails: string;
  childUploadDate: string;
  childCreatedBy: string;
  childStatusBackgroundColor: string;
  childStatusText: string | React.ReactElement;
}

interface TablePagination {
  current: number;
  pageSize: number;
  total?: number;
}

const ReportsTabPage = observer(() => {
  const { t } = useTranslation();
  const { stationStore , reportStore } = useStores();
  const stationId = stationStore.currentStation?.id || '';
  const { isMobile } = useResponsive();
  
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportRecord | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedReportForDelete, setSelectedReportForDelete] = useState<ReportRecord | null>(null);
  
  const onPublishClick = (record: ReportRecord) => {
    setSelectedReport(record);
    setIsPublishModalOpen(true);
  };
  const onClosePublishModal = () => {
    setIsPublishModalOpen(false);
    setSelectedReport(null);
  };

  const onDeleteClick = (record: ReportRecord) => {
    setSelectedReportForDelete(record);
    setIsDeleteModalOpen(true);
  };
  const onCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedReportForDelete(null);
  };
  
  const handleRefreshReports = () => {
    reportStore.getReports(reportStore.pagination.page, reportStore.pagination.pageSize);
  };

  const columns = getColumns(t, onPublishClick, reportStore, handleRefreshReports, onDeleteClick);
  
  useEffect(() => {
    if (stationId) {
      reportStore.getReports(1, reportStore.pagination.pageSize);
    }
  }, [stationId]);

  const handleTableChange = (pagination: TablePagination) => {
    const { current, pageSize } = pagination;
    reportStore.pagination.page = current;
    reportStore.pagination.pageSize = pageSize;
    reportStore.getReports(current, pageSize);
  };

  const reportsData = reportStore.reports?.data || [];
  
  const dataSource = reportsData.map((item: ReportRecord, index: number) => {
    const transformedChildren: TransformedChildRecord[] = (item.versions || []).map((child: ReportRecord) => ({
      ...child,
      childReportDetails: child.title || child.reportDetails || '',
      childUploadDate: dayjs(child.uploadDate as string || '').format('YYYY-MM-DD HH:mm:ss'),
      childCreatedBy: (child.userName as string) || child.createdBy || '',
      childStatusBackgroundColor: getBackGroundColorBasedOnStatus(child.status as ReportGeneratorStatus),
      childStatusText: getTextBasedOnStatus(child.status as unknown as ReportGeneratorStatus, t),
    }));

    return {
      key: item.id || index,
      reportDetails: (item.title || item.reportDetails || '') || '',
      dateCreated: dayjs(item.dateCreated || item.createdAt || item.createdDate || '')?.format('YYYY-MM-DD HH:mm:ss'),
      createdBy: item.reportCreatedBy || item.createdBy || '',
      status: item.status || '',
      _children: transformedChildren.sort((a: TransformedChildRecord, b: TransformedChildRecord) => {
        return dayjs(b.childUploadDate).diff(dayjs(a.childUploadDate));
      }),
      ...item,
    };
  });
  const totalReports = reportStore.reports?.count || 0;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateNewReportModalOpen, setIsCreateNewReportModalOpen] = useState(false);

  const onCreateReportClick = () => {
    setIsModalOpen(true);
  };
  const onCloseModal = () => {
    setIsModalOpen(false);
  };
  const onTryNewReport = () => {
    setIsCreateNewReportModalOpen(true);
  };
  const onCloseCreateNewReportModal = () => {
    setIsCreateNewReportModalOpen(false);
  };

  const canGenerateReport = reportStore.canGenerateReport;
  const canViewPeriodicReports = reportStore.canOnlyViewPeriodicReports;

  const canOnlyViewPeriodicReports = canViewPeriodicReports && !canGenerateReport;


  if (reportStore.isLoadingGetReports) {
    return (
      <ReportsTabPageContainer>
        <ValidationMessagePopoverGlobalStyle />
        <div className="engine-header-container">
          <EcoSelectStation />
        </div>
        <SpinCentered />  
      </ReportsTabPageContainer>
    );
  }

  if (reportStore.isLoadingGenerateDeletePublishReport) {
    return (
      <ReportsTabPageContainer>
        <ValidationMessagePopoverGlobalStyle />
        <div className="engine-header-container">
          <EcoSelectStation />
        </div>
        <SpinCentered />  
      </ReportsTabPageContainer>
    );
  }

  return (
    <ReportsTabPageContainer>
      <ValidationMessagePopoverGlobalStyle />
      <div className="engine-header-container">
        <EcoSelectStation />
      </div>
      <div className="reports-header-row">
        <div className="total-reports-text">
          <span className="total-reports-text-title">{t('totalReports')}</span>
          <span className="total-reports-count">({totalReports})</span>
        </div>
        <div className="header-right-section">
          {canGenerateReport && (
          <div className="create-report-button-container">
            <ReportPopup open={isModalOpen} onClose={onCloseModal} onTryNewReport={onTryNewReport}>
              <Button type="primary" onClick={onCreateReportClick} disabled={!canGenerateReport}>
                + {t('createNewReport')}
              </Button>
            </ReportPopup>
          </div>
          )}
        </div>
      </div>
      <div className="table-container">
        <StyledTable
          columns={columns}
          dataSource={dataSource}
          scroll={{ 
            x: isMobile ? 800 : undefined,
            y: dataSource.length > 0 ? (isMobile ? 'calc(100vh - 280px)' : 'calc(100vh - 340px)') : undefined
          }}
          pagination={{
            current: reportStore.pagination.page,
            pageSize: reportStore.pagination.pageSize,
            total: reportStore.pagination.total,
            showSizeChanger: !isMobile,
            showTotal: isMobile ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total} ${t('reports')}`,
            pageSizeOptions: ['10', '20', '50', '100'],
            simple: isMobile,
          }}
          onChange={handleTableChange}
          rowKey="key"
          bordered
          showHeader={true}
          locale={{
            emptyText: t('noReportsAvailable'), 
          }}
          expandable={canOnlyViewPeriodicReports ? undefined : {
            expandedRowRender: (record: ReportRecord & { _children?: TransformedChildRecord[]; key: string | number }) => {
              if (!record?._children?.length) return null;
          
              return (
                <div>
                  {record._children.map((child: TransformedChildRecord, index: number) => {
                    const onViewClickHandler = async () => {
                      child.id = record.id;
                      await handleEditClick(child, reportStore, t);
                    };

                    return (
                      <ChildRow key={`${record.key}-child-${index}`}>
                        <span className="child-row-report-details">{child.childReportDetails}</span>
            
                        <span className="child-row-date">{child.childUploadDate}</span>
            
                        <span className="child-row-created-by">{child.childCreatedBy}</span>
            
                        <span className="child-row-status">
                          <div className="child-row-status-content">
                            <StatusDot $backgroundColor={child.childStatusBackgroundColor} />
                            {child.childStatusText}
                          </div>
                        </span>
            
                        <span className="child-row-actions">
                          <Button className="child-row-view-button" type="text" onClick={onViewClickHandler}>
                            {t('View')}
                          </Button>
                          
                        </span>
                      </ChildRow>
                    );
                  })}
                </div>
              );
            },
          
            rowExpandable: (record: ReportRecord & { _children?: ReportRecord[] }) => record?._children?.length > 0,
          
            expandIcon: ({ expanded, onExpand, record }) => {
              if (!(record as ReportRecord & { _children?: ReportRecord[] })?._children?.length) return null;
          
              return (
                <ExpandIcon $expanded={expanded} onClick={(e) => onExpand(record, e)}>
                  <RightOutlined />
                </ExpandIcon>
              );
            },
          
            expandIconColumnIndex: 0,
            indentSize: 0,
          }}
          
        />
      </div>
      <CreateNewReportModal open={isCreateNewReportModalOpen} onClose={onCloseCreateNewReportModal} />
      <PublishModal open={isPublishModalOpen} onClose={onClosePublishModal} selectedReport={selectedReport} />
      <DeleteReportModal
        open={isDeleteModalOpen}
        onClose={onCloseDeleteModal}
        reportId={selectedReportForDelete?.id || null}
        onSuccess={handleRefreshReports}
      />
    </ReportsTabPageContainer>
  );
});

export default ReportsTabPage;
